import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FlexBetween from "components/FlexBetween";
import { useTheme } from "@emotion/react";
import { GetStationDetailById, GetFinalStationList } from "api/stations";
import { GetRouteListByStation } from "api/routes";
import {
  GetSumPackagesForBulkMove,
  GetPackageDetailByTracking,
  scanPackage,
} from "api/packages";
import PackageEditDialog from "components/PackageEditDialog";
import Swal from "sweetalert2";
import { DataGridCustomToolbarClientMode } from "components/DataGridCustomToolbar";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import StyledDataGrid from "components/bento/StyledDataGrid";
import { BG_PURPLE_TINT, PURPLE_MAIN, TEXT_SUBTLE } from "components/bento/bentoTokens";
import {
  copyTextToClipboard,
  getScanText,
  playAlertSound,
  playCorrectSound,
  playIncorrectSound,
} from "utils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import useStationRefresh, { pickDefaultStation } from "hooks/useStationRefresh";

const BulkMove = () => {
  const theme = useTheme();
  //   const [stationList, setStationList] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [rows, setRows] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const isNonMobile = useMediaQuery("(min-width: 600px)");
  const [routeList, setRouteList] = useState([]);
  const [routeId, setRouteId] = useState("");
  const [stationList, setStationList] = useState([]);

  // values to be sent to the backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  // Parcels the operator unticked — everything else is moved (new loads start ticked).
  const [unchecked, setUnchecked] = useState(new Set());
  const [editPackage, setEditPackage] = useState(null);

  const { user: _user, defaultStation: _defaultStation, ready } = useStationRefresh();

  function RefreshPackagesList(routeId, station) {
    setRows([]);
    setIsLoading(true);
    GetSumPackagesForBulkMove(routeId, station).then((res) => {
      if (res.success) {
        setRows(Array.isArray(res.data) ? res.data.filter((r) => r?._id?.customer?._id) : []);
      }
      setIsLoading(false);
    });
  }

  // Station change -> load only the routes passing through it, auto-select the first,
  // then load parcels for that route. Route changes reload via handleRouteChange, so
  // picking another route no longer snaps back to the first one.
  useEffect(() => {
    if (!selectedStation) return;
    let cancelled = false;
    GetRouteListByStation(selectedStation.id).then((res) => {
      if (cancelled) return;
      const routes = res.success && Array.isArray(res.data) ? res.data : [];
      const rid = routes[0]?.id || "";
      setRouteList(routes);
      setRouteId(rid);
      RefreshPackagesList(rid, selectedStation.id);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedStation]);

  const handleRouteChange = (rid) => {
    setRouteId(rid);
    if (selectedStation) RefreshPackagesList(rid, selectedStation.id);
  };

  // Backend ignores `search`, so filter the loaded groups here (same as mobile-staff):
  // match by customer name/WhatsApp or any tracking. Tracking hits auto-expand + highlight.
  const q = search.trim().toLowerCase();
  const isTrackingHit = (tracking) =>
    !!q && !!tracking && tracking.toLowerCase().includes(q);

  const visibleRows = useMemo(() => {
    if (!q) return rows || [];
    return (rows || []).filter((r) => {
      const c = r._id.customer;
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.whatsapp || "").toLowerCase().includes(q) ||
        (r.packageDetail || []).some((p) => isTrackingHit(p.tracking))
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, q]);

  useEffect(() => {
    if (!q) return;
    const hits = (rows || [])
      .filter((r) => (r.packageDetail || []).some((p) => isTrackingHit(p.tracking)))
      .map((r) => r._id.customer._id);
    setExpanded(new Set(hits));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, q]);

  const pkgKey = (p) => p._id || p.tracking;

  const toggleChecked = (key) => {
    setUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleEditClick = async (tracking) => {
    const res = await GetPackageDetailByTracking(tracking);
    if (res.status === 200 && res.data) setEditPackage(res.data);
  };

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    setSelectedStation(_defaultStation);
    // Route list is driven entirely by the selected-station effect above
    // (GetRouteListByStation), so it only ever shows routes for that station.

    GetFinalStationList().then((res) => {
      if (res.success) {
        setStationList(res.data);
        let arr = res.data;
        let validStation = [];
        if (Array.isArray(arr)) {
          arr.forEach((finalStation) => {
            // console.log("finalStation", finalStation);
            (_user?.stations || []).forEach((userStation) => {
              // console.log("userStation", userStation);
              if (finalStation.id === userStation.id)
                validStation.push(finalStation);
            });
          });
        }
        if (validStation) setStationList(validStation);
        setSelectedStation(pickDefaultStation(validStation, _defaultStation));
      }
    });
    // Re-runs once the refreshed profile/default station lands (`ready`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const handleBulkMove = async (data, selectedStation) => {
    const allPackages = data.packageDetail || [];
    const packageDetail = allPackages.filter((p) => !unchecked.has(pkgKey(p)));
    if (!packageDetail.length) return;
    setIsProcessing(true);
    let success = 0;
    let failed = 0;
    let errMsg = ""; // Initialize the error message variable

    for (let index = 0; index < packageDetail.length; index++) {
      let actionType = "bulkMove";
      try {
        const res = await scanPackage(
          getScanText(packageDetail[index].tracking),
          selectedStation.id,
          actionType
        );

        if (res && res.success) {
          console.log(`[ success ] - ${res.data.tracking}`);
          success++;
        } else {
          console.log(`[ failed!! ] - ${res.data.message}`);
          failed++;
          errMsg += `Failed to move ${packageDetail[index].tracking}: ${res.data.message}\n`; // Append error message
        }
      } catch (error) {
        console.error(
          `Error during scanPackage for ${packageDetail[index].tracking}:`,
          error
        );
        failed++;
        errMsg += `Failed to move ${packageDetail[index].tracking}: ${
          error.message || "Unknown error"
        }\n`; // Append error message for catch
      }
    }

    let customer = data._id.customer;
    console.log(success, failed);

    if (success === packageDetail.length) {
      RefreshPackagesList(routeId, selectedStation.id);
      playCorrectSound();
      Swal.fire({
        icon: "success",
        title: `Success!`,
        text: `${success} packages for ${customer.name} has been moved to ${selectedStation.id}`,
        timer: 3000,
      });
    } else if (success >= 1 && success < packageDetail.length) {
      playAlertSound();
      Swal.fire({
        title: `Fails ${failed} out of ${packageDetail.length} item(s)`,
        icon: "warning",
        text: errMsg, // Display the error message
      });
    } else {
      playIncorrectSound();
      Swal.fire({
        title: `All ${packageDetail.length} item(s) failed`,
        icon: "error",
        text: errMsg, // Display the error message
      });
    }
    setIsProcessing(false);
  };

  const columns = [
    {
      field: "_id",
      headerName: "ID",
      flex: 1,
      valueGetter: (params) => params.row._id.customer._id,
      hide: true,
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 0.7,
      valueGetter: (params) => params.row._id.customer.name,
    },
    {
      field: "whatsapp",
      headerName: "Whatsapp",
      flex: 1,
      valueGetter: (params) => params.row._id.customer.whatsapp,
      // renderCell: (cellValues) => {
      //   return (
      //     <IconButton
      //       variant="outlined"
      //       onClick={() => {
      //         copyTextToClipboard(cellValues.row.customer.whatsapp);
      //       }}
      //       sx={{ "&:hover": { color: theme.palette.warning.main } }}
      //     >
      //       {cellValues.row.customer.whatsapp}
      //     </IconButton>
      //   );
      // },
    },

    {
      field: "smsMessage",
      headerName: "Message",
      flex: 3,
      hide: true,
    },
    {
      field: "totalPackages",
      headerName: "item(s).",
      flex: 0.5,
    },
    {
      field: "tracking",
      headerName: "Tracking / Fee",
      flex: 3,
      sortable: false,
      valueGetter: (params) =>
        (params.row.packageDetail || []).map((p) => p.tracking).join(", "),
      renderCell: (cellValues) => {
        const key = cellValues.row._id.customer._id;
        const open = expanded.has(key);
        const list = cellValues.row.packageDetail || [];
        const selCount = list.filter((p) => !unchecked.has(pkgKey(p))).length;
        return (
          <Box width="100%" py={open ? 0.5 : 0}>
            <Box
              display="flex"
              alignItems="center"
              sx={{ cursor: "pointer" }}
              onClick={() => toggleExpand(key)}
            >
              {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              <Typography variant="body2" color="text.secondary">
                {list.length} parcel(s)
                {selCount < list.length && ` · ${selCount}/${list.length} selected`}
              </Typography>
            </Box>
            {open &&
              list.map((p, i) => {
                const hit = isTrackingHit(p.tracking);
                const pk = pkgKey(p);
                const checked = !unchecked.has(pk);
                return (
                  <Box
                    key={pk}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    pr={0.5}
                    borderRadius="6px"
                    sx={{
                      ...(hit ? { background: BG_PURPLE_TINT, color: PURPLE_MAIN, fontWeight: 700 } : {}),
                      ...(!checked ? { color: TEXT_SUBTLE, textDecoration: "line-through" } : {}),
                    }}
                  >
                    <Checkbox
                      size="small"
                      color="secondary"
                      checked={checked}
                      onChange={() => toggleChecked(pk)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0.5 }}
                    />
                    <Typography variant="caption" sx={{ width: 18, color: TEXT_SUBTLE }}>
                      {i + 1}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: "inherit", color: "inherit" }}>
                      {p.tracking}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "inherit", color: "inherit" }}>
                      {p.shippingFee ? `฿${p.shippingFee.toLocaleString()}` : "—"}
                    </Typography>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(p.tracking);
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
          </Box>
        );
      },
    },
    {
      field: "totalShippingFee",
      headerName: "Fee",
      flex: 0.5,
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      flex: 0.5,
    },
    {
      field: "copy",
      headerName: "Copy",
      flex: 0.5,
      hide: !isNonMobile,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outlined"
            onClick={() => {
              copyTextToClipboard(cellValues.row.smsMessage);
            }}
            sx={{ "&:hover": { color: theme.palette.warning.main } }}
          >
            <ContentCopyIcon />
          </IconButton>
        );
      },
    },
    {
      field: "move",
      headerName: "Move",
      flex: 0.5,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outlined"
            disabled={
              isProcessing ||
              !(cellValues.row.packageDetail || []).some((p) => !unchecked.has(pkgKey(p)))
            }
            onClick={() => {
              handleBulkMove(cellValues.row, selectedStation);
            }}
            sx={{ "&:hover": { color: theme.palette.warning.main } }}
          >
            <MoveUpIcon />
          </IconButton>
        );
      },
    },
  ];

  return (
    <PageShell title="Bulk Move" subtitle="Move parcels to the next station, group by customer.">
      <BentoCard sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
        <FlexBetween>
          {/* Filtering section */}
          <Grid container spacing={2}>
            <Grid item xl={3} md={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select station</InputLabel>
                <Select
                  label="Select station"
                  value={selectedStation ? selectedStation.id : ""}
                  onChange={(e) => {
                    GetStationDetailById(e.target.value).then((res) => {
                      if (res.success) {
                        setSelectedStation(res.data);
                      } else {
                        console.log(res.message);
                      }
                    });
                  }}
                  required
                >
                  {stationList &&
                    stationList.map(({ id, name }) => {
                      return (
                        <MenuItem key={id} value={id}>
                          {id} ({name})
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xl={3} md={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel>Route</InputLabel>
                <Select
                  id="route"
                  value={!routeId ? "" : routeId}
                  label="Route"
                  onChange={(event) => handleRouteChange(event.target.value)}
                  required
                >
                  {routeList &&
                    routeList.map(({ id, name }) => {
                      return (
                        <MenuItem key={id} value={id}>
                          {id} ({name})
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </FlexBetween>
      </BentoCard>

      {/*  Data Grid  */}
      <Box height="77vh" width="100%">
        <StyledDataGrid
          loading={isLoading}
          getRowId={(row) => row._id.customer._id}
          rows={visibleRows}
          columns={columns}
          rowCount={visibleRows.length}
          getRowHeight={({ id }) => (expanded.has(id) ? "auto" : null)}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          sortingMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          // onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{ Toolbar: DataGridCustomToolbarClientMode }}
          componentsProps={{
            toolbar: { search, setSearch },
          }}
          containerSx={{ height: "100%" }}
          autoHeight={false}
        />
      </Box>
      <PackageEditDialog
        open={!!editPackage}
        onClose={() => setEditPackage(null)}
        packageData={editPackage}
        onSuccess={() => {
          setEditPackage(null);
          if (selectedStation) RefreshPackagesList(routeId, selectedStation.id);
        }}
      />
    </PageShell>
  );
};

export default BulkMove;
